<template>
  <div class="reference-upload-container">
    <!-- File Upload Area -->
    <div 
      class="upload-area"
      :class="{ 'drag-over': isDragOver, 'uploading': isUploading }"
      @click="triggerFileInput"
      @dragover.prevent="handleDragOver"
      @dragleave.prevent="handleDragLeave"
      @drop.prevent="handleDrop"
    >
      <input
        ref="fileInput"
        type="file"
        multiple
        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.txt"
        @change="handleFileSelect"
        class="hidden-file-input"
      />
      
      <!-- Upload Icon and Text -->
      <div v-if="!isUploading" class="upload-content">
        <svg class="upload-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
        </svg>
        <div class="upload-text">
          <p class="upload-title">Upload Reference Documents</p>
          <p class="upload-subtitle">
            Drag & drop files here, or <span class="upload-link">click to browse</span>
          </p>
          <p class="upload-info">
            Supports: PDF, Word, Images (JPG, PNG, GIF), Text files • Max 10MB per file
          </p>
        </div>
      </div>
      
      <!-- Upload Progress -->
      <div v-else class="upload-progress">
        <div class="progress-spinner">
          <svg class="animate-spin" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
        <p class="progress-text">Uploading {{ uploadingFiles.length }} file{{ uploadingFiles.length !== 1 ? 's' : '' }}...</p>
      </div>
    </div>
    
    <!-- Error Messages -->
    <div v-if="uploadErrors.length > 0" class="error-messages">
      <div v-for="error in uploadErrors" :key="error.id" class="error-message">
        <svg class="error-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
        <span>{{ error.message }}</span>
        <button @click="removeError(error.id)" class="error-close">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>
    </div>
    
    <!-- Success Messages -->
    <div v-if="successMessage" class="success-message">
      <svg class="success-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
      </svg>
      <span>{{ successMessage }}</span>
    </div>
  </div>
</template>

<script>
import { reportUpgradeRequired } from '../services/ApiService.js'

export default {
  name: 'ReferenceDocumentUpload',
  props: {
    entryId: {
      type: [String, Number],
      required: true
    },
    entryType: {
      type: String,
      required: true,
      validator: (value) => ['education', 'experience', 'activity'].includes(value)
    },
    maxFiles: {
      type: Number,
      default: 10
    },
    maxFileSize: {
      type: Number,
      default: 10 * 1024 * 1024 // 10MB
    }
  },
  emits: ['upload-success', 'upload-error'],
  data() {
    return {
      isDragOver: false,
      isUploading: false,
      uploadingFiles: [],
      uploadErrors: [],
      successMessage: '',
      allowedTypes: [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'image/jpeg',
        'image/jpg', 
        'image/png',
        'image/gif',
        'text/plain'
      ]
    }
  },
  methods: {
    triggerFileInput() {
      if (!this.isUploading) {
        this.$refs.fileInput.click()
      }
    },

    handleDragOver() {
      this.isDragOver = true
    },

    handleDragLeave() {
      this.isDragOver = false
    },

    handleDrop(event) {
      this.isDragOver = false
      const files = Array.from(event.dataTransfer.files)
      this.processFiles(files)
    },

    handleFileSelect(event) {
      const files = Array.from(event.target.files)
      this.processFiles(files)
      // Clear the input so the same file can be selected again
      event.target.value = ''
    },

    processFiles(files) {
      // Clear previous messages
      this.clearMessages()
      
      // Validate files
      const validFiles = []
      const errors = []

      files.forEach(file => {
        // Check file type
        if (!this.allowedTypes.includes(file.type)) {
          errors.push({
            id: Date.now() + Math.random(),
            message: `${file.name}: Unsupported file type. Please use PDF, Word, images, or text files.`
          })
          return
        }

        // Check file size
        if (file.size > this.maxFileSize) {
          const sizeMB = (this.maxFileSize / (1024 * 1024)).toFixed(0)
          errors.push({
            id: Date.now() + Math.random(),
            message: `${file.name}: File too large. Maximum size is ${sizeMB}MB.`
          })
          return
        }

        validFiles.push(file)
      })

      // Show validation errors
      if (errors.length > 0) {
        this.uploadErrors = errors
        return
      }

      // Check total file limit
      if (validFiles.length > this.maxFiles) {
        this.uploadErrors = [{
          id: Date.now(),
          message: `Too many files selected. Maximum is ${this.maxFiles} files at once.`
        }]
        return
      }

      // Upload valid files
      if (validFiles.length > 0) {
        this.uploadFiles(validFiles)
      }
    },

    getCvId() {
      // Get CV ID from the window configuration set by Rails
      if (typeof window !== 'undefined' && window.cvCreatorConfig && window.cvCreatorConfig.cvId) {
        return window.cvCreatorConfig.cvId
      }
      return null
    },

    async uploadFiles(files) {
      // Don't allow upload if no entry ID
      if (!this.entryId) {
        this.uploadErrors = [{
          id: Date.now(),
          message: 'Cannot upload documents for unsaved entries. Please save your CV first to enable document uploads.'
        }]
        this.$emit('upload-error', { message: 'Entry must be saved before uploading documents' })
        return
      }

      const cvId = this.getCvId()
      if (!cvId) {
        this.uploadErrors = [{
          id: Date.now(),
          message: 'Cannot upload documents: No CV ID available. Please save your CV first.'
        }]
        this.$emit('upload-error', { message: 'CV ID required for document upload' })
        return
      }

      this.isUploading = true
      this.uploadingFiles = files.map(file => ({ name: file.name, size: file.size }))
      
      try {
        const formData = new FormData()
        
        // Add files to form data
        files.forEach(file => {
          formData.append('reference_documents[]', file)
        })
        
        // Add entry information
        formData.append('entry_type', this.entryType)
        formData.append('entry_id', this.entryId)

        const response = await fetch(`/api/cvs/${cvId}/${this.entryType}_entries/${this.entryId}/reference_documents`, {
          method: 'POST',
          headers: {
            'X-CSRF-Token': this.getCSRFToken(),
            'Accept': 'application/json'
          },
          body: formData
        })

        if (response.ok) {
          const data = await response.json()
          this.successMessage = `Successfully uploaded ${files.length} file${files.length !== 1 ? 's' : ''}`
          this.$emit('upload-success', data)

          // Clear success message after 3 seconds
          setTimeout(() => {
            this.successMessage = ''
          }, 3000)
        } else if (response.status === 402) {
          // Pro feature — open the global UpgradeModal
          const errorData = await response.json().catch(() => ({}))
          reportUpgradeRequired(errorData)
          throw new Error(errorData.message || 'Upgrade required')
        } else {
          const errorData = await response.json()
          throw new Error(errorData.message || 'Upload failed')
        }
      } catch (error) {
        console.error('Upload error:', error)
        this.uploadErrors = [{
          id: Date.now(),
          message: error.message || 'Failed to upload files. Please try again.'
        }]
        this.$emit('upload-error', error)
      } finally {
        this.isUploading = false
        this.uploadingFiles = []
      }
    },

    removeError(errorId) {
      this.uploadErrors = this.uploadErrors.filter(error => error.id !== errorId)
    },

    clearMessages() {
      this.uploadErrors = []
      this.successMessage = ''
    },

    getCSRFToken() {
      const meta = document.querySelector('meta[name="csrf-token"]')
      return meta ? meta.getAttribute('content') : ''
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    }
  }
}
</script>

<style scoped>
.reference-upload-container {
  margin-top: 1rem;
}

.upload-area {
  border: 2px dashed #d1d5db;
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: #f9fafb;
  position: relative;
}

.upload-area:hover {
  border-color: #000537;
  background: #f6f6ff;
}

.upload-area.drag-over {
  border-color: #000537;
  background: #fff7ed;
  transform: scale(1.02);
}

.upload-area.uploading {
  cursor: not-allowed;
  opacity: 0.7;
}

.hidden-file-input {
  display: none;
}

.upload-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.upload-icon {
  width: 3rem;
  height: 3rem;
  color: #6b7280;
}

.upload-text {
  color: #374151;
}

.upload-title {
  font-size: 1.125rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.upload-subtitle {
  font-size: 0.875rem;
  margin-bottom: 0.5rem;
}

.upload-link {
  color: #000537;
  font-weight: 500;
}

.upload-info {
  font-size: 0.75rem;
  color: #6b7280;
}

.upload-progress {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.progress-spinner svg {
  width: 2rem;
  height: 2rem;
  color: #000537;
}

.progress-text {
  font-size: 0.875rem;
  color: #374151;
  font-weight: 500;
}

.error-messages {
  margin-top: 1rem;
}

.error-messages > .error-message:not(:last-child) {
  margin-bottom: 0.5rem;
}

.error-message {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: #dc2626;
  font-size: 0.875rem;
}

.error-icon {
  width: 1rem;
  height: 1rem;
  flex-shrink: 0;
}

.error-close {
  margin-left: auto;
  padding: 0.25rem;
  color: #dc2626;
  background: none;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.error-close:hover {
  background: #fee2e2;
  color: #b91c1c;
}

.success-message {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem;
  margin-top: 1rem;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 8px;
  color: #16a34a;
  font-size: 0.875rem;
}

.success-icon {
  width: 1rem;
  height: 1rem;
  flex-shrink: 0;
}

/* Dark mode support */
.dark .upload-area {
  background: #374151;
  border-color: #4b5563;
  color: #d1d5db;
}

.dark .upload-area:hover,
.dark .upload-area.drag-over {
  background: #4b5563;
  border-color: #f97316;
}

.dark .upload-icon {
  color: #9ca3af;
}

.dark .upload-text {
  color: #d1d5db;
}

.dark .upload-info {
  color: #9ca3af;
}

.dark .progress-text {
  color: #d1d5db;
}

.dark .error-message {
  background: #7f1d1d;
  border-color: #dc2626;
  color: #fca5a5;
}

.dark .success-message {
  background: #14532d;
  border-color: #16a34a;
  color: #bbf7d0;
}
</style>